import React from 'react';
import { motion } from 'framer-motion';
import {
  SiPython,
  SiFirebase,
  SiNextdotjs,
  SiReact,
  SiTailwindcss,
  SiTypescript,
  SiDocker,
  SiGooglecloud,
  SiJson
} from 'react-icons/si';
import { FaGithub, FaExternalLinkAlt } from 'react-icons/fa';
import CustomCursor from '../components/CustomCursor';
import FloatingTechIcons from '../components/FloatingTechIcons';

const projects = [
  {
    title: "Password Manager",
    description: "A secure desktop app to store, manage, and suggest strong passwords. Data is saved locally in JSON.",
    tech: [<SiPython />, <SiJson />],
    status: "Completed",
    github: "",
    demo: null,
  },
  {
    title: "Personal Portfolio",
    description: "This very portfolio site with custom UI sections, and React + Tailwind powered layout.",
    tech: [<SiReact />, <SiTailwindcss />],
    status: "In Development",
    github: null,
    demo: null,
  },
  {
    title: "Full Stack Video App",
    description: "A YouTube-style app using Firebase Auth, Cloud Firestore, Cloud Run & FFmpeg for video upload/transcode.",
    tech: [<SiTypescript />, <SiNextdotjs />, <SiFirebase />, <SiGooglecloud />, <SiDocker />],
    status: "Planned",
    github: null,
    demo: null,
  },
];

const statusColor = {
  "Completed": "bg-green-600",
  "In Development": "bg-yellow-500",
  "Planned": "bg-gray-500",
};

const Projects = () => {
  return (
    <section className="bg-animated-gradient text-white pt-28 pb-36 px-4 sm:px-6 md:px-8 min-h-screen" id="projects">
      <CustomCursor />
      <div className="max-w-7xl mx-auto">
        {/* Heading */}
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center mt-16 sm:mt-20 mb-6 sm:mb-10 text-purple-400">
          My Projects
        </h2>

        {/* Subtext */}
        <p className="text-gray-300 text-center max-w-2xl sm:max-w-4xl mx-auto mb-10 text-base sm:text-lg px-2">
          Here are a few of my projects highlighting my full-stack development skills, 
          where I’ve focused on building responsive, scalable, and user-friendly web applications.
        </p>

        {/* Project Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10">
          {projects.map((project, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-[#161b22] rounded-xl p-5 sm:p-6 shadow-lg hover:shadow-purple-500/30 transition-all duration-300 border border-gray-700"
            >
              {/* Title and Status */}
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-lg sm:text-xl font-semibold">{project.title}</h3>
                <span className={`text-xs sm:text-sm px-2 sm:px-3 py-1 rounded-full ${statusColor[project.status]}`}>
                  {project.status}
                </span>
              </div>

              {/* Description */}
              <p className="text-xs sm:text-sm text-gray-300 mb-4">{project.description}</p>

              {/* Tech Stack Icons */}
              <div className="flex flex-wrap gap-2 text-purple-300 text-lg sm:text-xl mb-6">
                {project.tech.map((icon, i) => (
                  <div key={i} className="hover:text-purple-500 transition">{icon}</div>
                ))}
              </div>

              {/* Buttons */}
              <div className="flex flex-wrap gap-3">
                <a
                  href={project.github || "#"}
                  className={`flex items-center justify-center gap-2 text-xs sm:text-sm px-3 py-2 sm:px-4 sm:py-2 rounded-md transition w-full sm:w-auto
                    ${project.github ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-700 cursor-not-allowed'}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaGithub /> GitHub
                </a>
                <a
                  href={project.demo || "#"}
                  className={`flex items-center justify-center gap-2 text-xs sm:text-sm px-3 py-2 sm:px-4 sm:py-2 rounded-md transition w-full sm:w-auto
                    ${project.demo ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-700 cursor-not-allowed'}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaExternalLinkAlt /> Demo
                </a>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Floating Tech Icons */}
        <div className="mt-12 sm:mt-16">
          <FloatingTechIcons />
        </div>
      </div>
    </section>
  );
};

export default Projects;
